import React, { useState } from "react";
import { motion } from "framer-motion";
import { Check, Info, Zap } from "lucide-react";

const creditPacks = [
  {
    name: "Basic",
    price: "$499",
    credits: "100,000",
    tagline: "Solo practices & light AI use",
    highlight: false,
    badge: null,
    features: [
      "100,000 AI credits / month",
      "Overage at $0.005 per credit",
      "All 21 AI agents included",
      "All software included",
    ],
  },
  {
    name: "Plus",
    price: "$799",
    credits: "200,000",
    tagline: "Most growing practices",
    highlight: true,
    badge: "Recommended",
    features: [
      "200,000 AI credits / month",
      "Overage at $0.005 per credit",
      "All 21 AI agents included",
      "All software included",
    ],
  },
  {
    name: "Pro",
    price: "$999",
    credits: "300,000",
    tagline: "High-volume",
    highlight: false,
    badge: null,
    features: [
      "300,000 AI credits / month",
      "Overage at $0.005 per credit",
      "All 21 AI agents included",
      "All software included",
    ],
  },
];

const PackCard = ({ plan, delay }) => {
  const accent = plan.highlight
    ? "border-blue-200 ring-1 ring-blue-100 shadow-[0_24px_60px_-22px_rgba(59,130,246,0.45)]"
    : "border-slate-200 shadow-[0_16px_44px_-20px_rgba(15,23,42,0.22)]";

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.5 }}
      className={`relative flex h-full flex-col rounded-2xl border bg-white p-5 ${accent}`}
    >
      {plan.badge && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
          <span className="inline-flex items-center rounded-full bg-gradient-to-r from-blue-600 to-indigo-600 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.16em] text-white shadow-md shadow-blue-500/30">
            {plan.badge}
          </span>
        </div>
      )}

      <header className="flex flex-col">
        <p className="text-[16px] font-semibold text-slate-900 leading-tight">
          {plan.name}
        </p>
        <p className="mt-1 text-[12px] leading-snug text-slate-500">
          {plan.tagline}
        </p>
      </header>

      <div className="mt-3 flex items-baseline gap-1.5">
        <span className="text-[36px] font-extrabold leading-none tracking-tight text-slate-900">
          {plan.price}
        </span>
        <span className="text-[13px] font-medium text-slate-500">/month</span>
      </div>

      <div className="mt-3 rounded-lg border border-slate-100 bg-slate-50/70 px-3 py-2">
        <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500">
          AI Credits
        </p>
        <p className="mt-0.5 text-[15px] font-bold text-slate-900">
          {plan.credits} <span className="font-medium text-slate-500 text-[12px]">/ month</span>
        </p>
      </div>

      <div className="mt-3 mb-3 border-t border-slate-100" />

      <ul className="space-y-1.5">
        {plan.features.map((f, i) => (
          <li key={i} className="flex items-start gap-2">
            <Check
              className={`mt-[3px] h-3.5 w-3.5 shrink-0 ${
                plan.highlight ? "text-blue-500" : "text-slate-700"
              }`}
            />
            <span className="text-[12.5px] leading-snug text-slate-700">{f}</span>
          </li>
        ))}
      </ul>
    </motion.div>
  );
};

const EngagementPricingSlide = () => {
  const [model, setModel] = useState("pms");
  const isIntegration = model === "integration";
  const baseSoftware = 1499;
  const integrationFee = isIntegration ? 499 : 0;
  const total = baseSoftware + integrationFee;

  return (
    <div className="h-full w-full bg-[#f8fafc] flex flex-col items-center justify-center p-6 xl:p-10 relative overflow-hidden font-sans">
      <div
        className="absolute top-[-200px] right-[-100px] w-[700px] h-[700px] rounded-full blur-[120px] opacity-30 pointer-events-none"
        style={{ background: "linear-gradient(135deg, #93c5fd 0%, #c4b5fd 50%, #fbcfe8 100%)" }}
      />
      <div
        className="absolute bottom-[-200px] left-[-100px] w-[600px] h-[600px] rounded-full blur-[120px] opacity-25 pointer-events-none"
        style={{ background: "linear-gradient(135deg, #a5f3fc 0%, #93c5fd 50%, #c4b5fd 100%)" }}
      />
      <div className="absolute inset-0 bg-[radial-gradient(#cbd5e1_0.8px,transparent_0.8px)] [background-size:28px_28px] opacity-40 pointer-events-none" />

      <div className="w-full flex flex-col items-center relative z-10 max-w-[1080px]">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-5"
        >
          <h2 className="text-3xl lg:text-[40px] font-bold text-slate-900 tracking-tight leading-tight">
            Simple,{" "}
            <span className="bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
              transparent pricing.
            </span>
          </h2>
        </motion.div>

        {/* Engagement model toggle */}
        <div className="mb-4 inline-flex items-center rounded-full border border-slate-200 bg-white p-1 shadow-sm">
          <button
            type="button"
            onClick={() => setModel("pms")}
            className={`relative px-4 py-1.5 text-[12.5px] font-semibold rounded-full transition-colors ${
              !isIntegration
                ? "bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-sm"
                : "text-slate-600 hover:text-slate-900"
            }`}
          >
            Asha PMS
          </button>
          <button
            type="button"
            onClick={() => setModel("integration")}
            className={`relative px-4 py-1.5 text-[12.5px] font-semibold rounded-full transition-colors ${
              isIntegration
                ? "bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-sm"
                : "text-slate-600 hover:text-slate-900"
            }`}
          >
            Continuous Integration
          </button>
        </div>

        {/* Inline pricing — base + (optional) integration */}
        <motion.div
          key={model}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="mb-6 text-center"
        >
          <div className="flex items-baseline justify-center gap-1.5">
            <span className="text-[44px] font-extrabold leading-none tracking-tight text-slate-900">
              ${total.toLocaleString()}
            </span>
            <span className="text-[14px] font-medium text-slate-500">/month base</span>
          </div>
          <p className="mt-2 text-[13px] text-slate-600">
            <strong className="text-slate-900">$1,499</strong> software &amp; platform
            {isIntegration ? (
              <>
                {" + "}
                <strong className="text-slate-900">$499</strong> PMS integration fee
              </>
            ) : (
              <span className="text-slate-500"> · no integration fee on Asha PMS</span>
            )}
          </p>
        </motion.div>

        {/* AI credit packs */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15, duration: 0.4 }}
          className="mb-3 flex items-center gap-2 text-[12px] font-bold uppercase tracking-[0.18em] text-slate-500"
        >
          <Zap className="h-3.5 w-3.5 text-blue-500" />
          AI plan
        </motion.div>

        <div className="w-full grid grid-cols-3 gap-4">
          {creditPacks.map((p, i) => (
            <PackCard key={p.name} plan={p} delay={0.2 + i * 0.08} />
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5, duration: 0.4 }}
          className="mt-5 flex items-center gap-2 text-[12px] font-medium text-slate-500"
        >
          <Info className="h-3.5 w-3.5 text-slate-400" />
          Overage on any pack is billed at <strong className="text-slate-700 ml-1">$0.005</strong> per credit.
        </motion.div>
      </div>
    </div>
  );
};

export default EngagementPricingSlide;
